import { useRef, useState, type ReactNode } from 'react';
import { SECTION_NAV } from '../../lib/copy';

export type SectionGroup = 'brief' | 'research' | 'notes';

export interface ReadingSection {
  key: string;
  label: string;
  content: ReactNode;
  group: SectionGroup;
  /** The facts behind a research section, beside its name in the navigator. */
  count?: number | null;
}

/**
 * **The Research tab's grid** (blend spec, step 4): the section navigator on
 * the left, one panel at a time in the middle, the record's properties on the
 * right. The run block above it sits on the same three columns.
 *
 * The middle column always exists — before any research it holds the way to
 * start one — so the grid is drawn whether or not there are sections to
 * navigate.
 */
export function ResearchGrid({
  nav,
  aside,
  children,
}: {
  nav?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="research-grid">
      <div className="research-nav">{nav}</div>
      <div className="research-main">{children}</div>
      {aside}
    </div>
  );
}

/**
 * The navigator and its panels: grouped Brief and Research, with each research
 * section's fact count. No Previous or Next: the navigator is the way between
 * panels. On a phone it folds to one dropdown naming the open section.
 *
 * While a run is under way, `pending` lists the research sections it has not
 * reached yet, greyed, and `briefLater` stands in for a brief not written yet.
 */
export function ReadingSections({
  sections,
  initialOpen,
  lead,
  aside,
  pending = [],
  briefLater = null,
}: {
  sections: ReadingSection[];
  initialOpen?: string;
  /** What sits above the panel in the middle column: questions, the way to start. */
  lead?: ReactNode;
  aside?: ReactNode;
  pending?: { key: string; label: string }[];
  briefLater?: string | null;
}) {
  const [selected, setSelected] = useState(initialOpen);
  const [folded, setFolded] = useState(true);
  const navRef = useRef<HTMLElement>(null);
  const active = sections.some((s) => s.key === selected) ? selected : sections[0]?.key;
  const current = sections.find((s) => s.key === active);

  const choose = (key: string) => {
    setSelected(key);
    setFolded(true);
    /* A long panel read to its end leaves the next one's head off the top of
       the screen; bring the grid back into view rather than open mid-page. */
    const top = navRef.current?.closest('.research-grid')?.getBoundingClientRect().top;
    if (top !== undefined && top < 0) window.scrollBy({ top: top - 72 });
  };

  const entry = (s: ReadingSection) => (
    <button
      type="button"
      key={s.key}
      aria-current={active === s.key ? 'true' : undefined}
      onClick={() => choose(s.key)}
    >
      <span className="l">{s.label}</span>
      {s.count ? <span className="n">{s.count}</span> : null}
    </button>
  );

  const brief = sections.filter((s) => s.group === 'brief');
  const research = sections.filter((s) => s.group === 'research');
  const notes = sections.filter((s) => s.group === 'notes');

  const nav =
    sections.length > 0 || pending.length > 0 ? (
      <nav
        ref={navRef}
        className={folded ? 'secnav' : 'secnav open'}
        aria-label="Research sections"
      >
        <button
          type="button"
          className="secnav-toggle"
          aria-expanded={!folded}
          onClick={() => setFolded((value) => !value)}
        >
          <span className="cur">{current?.label ?? SECTION_NAV.phoneLabel}</span>
          {current?.count ? <span className="n">{current.count}</span> : null}
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            aria-hidden="true"
          >
            <path d="M3.5 4.75 6 7.25l2.5-2.5" />
          </svg>
        </button>
        <div className="groups">
          {brief.length > 0 || briefLater ? (
            <div className="grp">
              <span className="sc">{SECTION_NAV.brief}</span>
              {brief.map(entry)}
              {brief.length === 0 && briefLater ? (
                <span className="later">{briefLater}</span>
              ) : null}
            </div>
          ) : null}
          {research.length > 0 || pending.length > 0 ? (
            <div className="grp">
              <span className="sc">{SECTION_NAV.research}</span>
              {research.map(entry)}
              {pending.map((p) => (
                <span className="yet" key={p.key}>
                  <span className="l">{p.label}</span>
                  <span className="n">{SECTION_NAV.notYet}</span>
                </span>
              ))}
            </div>
          ) : null}
          {notes.length > 0 ? <div className="grp">{notes.map(entry)}</div> : null}
        </div>
      </nav>
    ) : null;

  return (
    <ResearchGrid nav={nav} aside={aside}>
      {lead}
      {sections.length > 0 ? (
        <div className="research-panels">
          {sections.map((s) => (
            <section key={s.key} hidden={active !== s.key} aria-label={s.label}>
              {s.content}
            </section>
          ))}
        </div>
      ) : null}
    </ResearchGrid>
  );
}
